import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useRef } from "react";

import { StyledAnchor } from "@/components/common/styles";
import { useTrustpilotWidget } from "@/components/landing/trustpilot";
import MarketingLottie from "./MarketingLottie";
import { HEADING_2 } from "./marketingType";
import { MID, NOT_WIDE, TINY, marketingPalette } from "./tokens";

/**
 * The green panel carrying Trustpilot's review carousel, on the home page and
 * on every country page. The two differ only in the band they sit in, so the
 * section wrapper stays with each page's own primitives.
 */

/**
 * Hidden until the panel is hovered, which is what the published page does. A
 * reader who has asked for less motion gets them at rest instead of a fade.
 */
const HEART_REVEAL = {
  opacity: 0,
  transition: "opacity 300ms ease",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
} as const;

/** Both hearts bleed off their own edge; only the side and the size differ. */
const HEART = {
  width: "432px",
  height: "432px",
  [NOT_WIDE]: { width: "360px", height: "360px" },
  [MID]: { width: "344px", height: "344px" },
  [TINY]: { width: "323px", height: "323px" },
};

type BandContent = {
  heading: string;
  lottie: string;
  height: number;
  href: string;
  widget: Record<string, string>;
};

export default function TrustpilotBand({
  content,
  heartClass,
}: {
  content: BandContent;
  heartClass: string;
}) {
  const widget = useRef<HTMLDivElement>(null);
  useTrustpilotWidget(widget);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        width: 1,
        p: "4rem 4rem 3rem",
        borderRadius: "2rem",
        backgroundColor: marketingPalette.trustPanel,
        overflow: "hidden",
        [`&:hover .${heartClass}`]: { opacity: 1 },
        [NOT_WIDE]: { p: "2rem 2rem 1rem" },
        [MID]: { p: "1rem" },
        [TINY]: { gap: "1rem", borderRadius: "1rem" },
      }}
    >
      <Typography component="h2" sx={{ ...HEADING_2, textAlign: "center" }}>
        {content.heading}
      </Typography>
      {/*
        The published page rests both of these at opacity 0 and fades them in
        when the panel is hovered, from an interaction engine we do not run.
        The trigger is a plain hover, so it is reproduced in CSS rather than
        approximated by drawing them at their end state.
      */}
      <Box
        aria-hidden
        className={heartClass}
        sx={{
          position: "absolute",
          inset: "0 -11% 0 auto",
          ...HEART,
          ...HEART_REVEAL,
        }}
      >
        <MarketingLottie
          path={content.lottie}
          loop
          play
          style={{ width: "100%", height: "100%", pointerEvents: "none" }}
        />
      </Box>
      <Box
        aria-hidden
        className={heartClass}
        sx={{
          position: "absolute",
          inset: "0 auto 0 -12%",
          ...HEART,
          ...HEART_REVEAL,
        }}
      >
        <MarketingLottie
          path={content.lottie}
          loop
          play
          style={{ width: "100%", height: "100%", pointerEvents: "none" }}
        />
      </Box>
      {/*
        Trustpilot's own carousel, on the published page's exact template and
        business unit. The reviews are theirs to serve: a first-party
        reproduction would be review copy we invented. `securityHeaders.ts`
        already carries widget.trustpilot.com in script-src, frame-src and
        connect-src, so nothing had to be loosened to embed it.

        The height is held here as well as passed to the widget: the bootstrap
        replaces this element's contents with its iframe, and without a box to
        replace into, the band collapses and then jumps 240px.

        The anchor is Trustpilot's documented fallback and the only thing a
        reader sees if the script never runs. It says what it links to rather
        than the bare word the snippet ships with.
      */}
      <Box
        ref={widget}
        className="trustpilot-widget"
        data-style-height={`${content.height}px`}
        {...content.widget}
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 1,
          height: `${content.height}px`,
        }}
      >
        <StyledAnchor
          href={content.href}
          target="_blank"
          rel="noopener"
          sx={{ color: marketingPalette.ink, fontSize: 16 }}
        >
          Read our reviews on Trustpilot
        </StyledAnchor>
      </Box>
    </Box>
  );
}
